import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { fillArray, nonNullable } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import { viewItemListExperiences } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import OfferListInfiniteScroll from 'components/OfferList/OfferListInfiniteScroll'
import Group from 'components/utils/Group'

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${rem(200)};
  position: relative;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

interface Props {
  placeId: string;
  sortBy?: Experiences.SortTypes;
  categoryCodes?: Array<number>;
  priceLte?: number;
  priceGte?: number;
  campaigns?: Array<string>;
  excludeIds?: Array<string>;
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>;
  collections?: Array<string>;
  from?: string;
  to?: string;
  minRating?: number;
  leExclusive?: boolean;
  travelStyles?: Array<Experiences.TravelStyleFilter>;
  featured?: Array<Experiences.FeaturedFilter>;
  timeSlots?: Array<Experiences.TimeSlotFilter>;
}

function ExperienceDenseSearchList(props: Props) {
  const {
    placeId,
    sortBy,
    categoryCodes,
    priceGte,
    priceLte,
    campaigns,
    excludeIds,
    luxPlusFeatures,
    collections,
    from,
    to,
    minRating,
    leExclusive,
    travelStyles,
    featured,
    timeSlots,
  } = props
  const [page, setPage] = useState<number>(1)

  useEffect(() => {
    setPage(1)
  }, [
    placeId,
    sortBy,
    categoryCodes,
    priceGte,
    priceLte,
    campaigns,
    luxPlusFeatures,
    collections,
    from,
    to,
    minRating,
    leExclusive,
    travelStyles,
    featured,
    timeSlots,
  ])

  const limit = Math.min(page * 32, 320)
  const [list, experiences] = useExperienceSearch(
    { placeId, limit, sortBy, excludeIds, campaigns, from, to },
    {
      categoryCodes,
      priceGte,
      priceLte,
      luxPlusFeatures,
      collections,
      minRating,
      leExclusive,
      travelStyles,
      featured,
      timeSlots,
    },
  )
  const hasMore = !!list && experiences.length < list.ids.length

  const sortedExperiences = useMemo(() => {
    if (sortBy !== 'bestValue') return experiences
    return [...experiences].sort(
      (a, b) => (b?.discount ?? 0) - (a?.discount ?? 0),
    )
  }, [experiences, sortBy])

  useEffect(() => {
    if (experiences?.length) {
      const event = viewItemListExperiences(nonNullable(experiences))
      if (event) {
        addGTMEvent(event)
      }
    }
  }, [experiences])

  const handleLoadMore = useCallback(() => setPage((prev) => prev + 1), [])

  return (
    <div>
      <ListGrid>
        {(!list || list.fetching) &&
          fillArray(8).map((id) => (
            <ExperienceCompactSearchTileSkeleton key={id} />
          ))}
        {list &&
          !list.fetching &&
          sortedExperiences.map((exp, idx) => (
            <Fragment key={`${exp?.id}-${idx}`}>
              {!exp && <ExperienceCompactSearchTileSkeleton />}
              {exp && (
                <ExperienceCompactSearchTile
                  experience={exp}
                  position={idx}
                  date={from && from === to ? from : undefined}
                  listKey={list.listKey}
                />
              )}
            </Fragment>
          ))}
      </ListGrid>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="center"
      >
        <OfferListInfiniteScroll
          onLoadMore={handleLoadMore}
          isLoading={list?.fetching}
          hasMore={hasMore}
        />
      </Group>
    </div>
  )
}

export default ExperienceDenseSearchList
